import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { App, Button, Card, Space } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedTransportForm from '../components/SharedTransportForm';
import { normalizeOrderOptions, type OrderOptionDto } from '../dto/order';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import { fetchSupplierOrders } from '../modules/orders/api/supplierOrdersApi';
import { canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { getTransportErrorMessage, isBlockingTransportError } from '../modules/transports/transportErrorMessages';
import { useFeedback } from '../utils/feedback';
import api from '../services/api';
import { authService } from '../services/auth';
import './TransportRouteFormPage.css';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { createTransportRoute } from '../modules/transports/api/transportRoutesApi';
import { buildOrderTransportPath, readTransportReturnContext } from '../modules/transports/transportReturnContext';

const CreateTransportRoute: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const feedback = useFeedback();
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetOrderId = searchParams.get('orderId') || undefined;
  const returnContext = readTransportReturnContext(searchParams);
  const initialOrderIds = useMemo(
    () => (presetOrderId ? [presetOrderId] : undefined),
    [presetOrderId],
  );
  const [availableOrders, setAvailableOrders] = useState<OrderOptionDto[]>([]);
  const scopedOrder = presetOrderId
    ? availableOrders.find((order) => order.id === presetOrderId)
    : undefined;
  // Etap 2 (transport przywozowy): candidates for the inbound (ZD) link.
  const [availableSupplierOrders, setAvailableSupplierOrders] = useState<SupplierOrderDto[]>([]);
  const [supplierOrdersLoading, setSupplierOrdersLoading] = useState(true);
  const [supplierOrdersLoadError, setSupplierOrdersLoadError] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canViewTransportCosts = authService.canViewTransportCosts();

  const fetchOrders = useCallback(async () => {
    try {
      // Ask for supplier purchase-value hints so the cargo "Wartość towaru"
      // column can prefill from the linked supplier orders. The backend strips
      // the hints for users without supplier-order purchase-price visibility, so
      // requesting them unconditionally is safe.
      const response = await api.get('/orders', {
        params: { includeSupplierPurchaseHints: 'true' },
      });
      setAvailableOrders(normalizeOrderOptions(response.data, 'transport.create.availableOrders'));
    } catch (error) {
      feedback.error(t('transport:errorFetchingOrders'));
    }
  }, [feedback, t]);

  // Etap 2: a failed supplier-order fetch keeps the outbound flow usable while
  // the inbound picker exposes an explicit error and retry action.
  const fetchSupplierOrdersForLinking = useCallback(async () => {
    setSupplierOrdersLoading(true);
    setSupplierOrdersLoadError(false);
    try {
      setAvailableSupplierOrders(await fetchSupplierOrders());
    } catch (error) {
      setAvailableSupplierOrders([]);
      setSupplierOrdersLoadError(true);
    } finally {
      setSupplierOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageTransports) {
      navigate('/app/transports', { replace: true });
      return;
    }

    void fetchOrders();
    void fetchSupplierOrdersForLinking();
  }, [canManageTransports, fetchOrders, fetchSupplierOrdersForLinking, navigate]);

  const handleSubmit = async (payload: Record<string, unknown>) => {
    setLoading(true);

    try {
      const response = await submitWithOperationalException(
        (confirmedPayload) => createTransportRoute(confirmedPayload),
        payload,
        modal,
        t,
      );
      if (!response) return;
      // The route number is server-assigned, so the confirmation can only name
      // the transport from the response.
      feedback.success(t('transport:routeCreatedSuccessfully', {
        number: response.data?.routeNumber,
      }));
      navigate(returnContext ? buildOrderTransportPath(returnContext.orderId) : `/app/transports/${response.data.id}`);
    } catch (error: any) {
      // Code -> Polish copy is decided once in
      // transportRouteViews.mapTransportMutationErrorToMessage, wrapped by
      // getTransportErrorMessage so every transport screen agrees.
      feedback.error(getTransportErrorMessage(error, t, 'transport:errorCreatingRoute'), {
        sticky: isBlockingTransportError(error),
      });
    } finally {
      setLoading(false);
    }
  };

  if (!canManageTransports) {
    return null;
  }

  return (
    <div className="transport-route-form-page">
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => { if (returnContext) navigate(buildOrderTransportPath(returnContext.orderId)); else navigate(-1); }}>
          {t('common:back')}
        </Button>
      </Space>

      <Card
        className="transport-route-form-shell-card"
        title={scopedOrder
          ? t('transport:scopedOrder.pageTitle', {
            number: scopedOrder.displayOrderNumber || scopedOrder.orderNumber,
          })
          : t('transport:createNewRoute')}
      >
        <SharedTransportForm
          availableOrders={availableOrders}
          availableSupplierOrders={availableSupplierOrders}
          supplierOrdersLoading={supplierOrdersLoading}
          orderScopeId={presetOrderId}
          supplierOrdersLoadError={supplierOrdersLoadError}
          onRetrySupplierOrders={fetchSupplierOrdersForLinking}
          initialOrderIds={initialOrderIds}
          canEditTransportCosts={canEditTransportCosts}
          canViewTransportCosts={canViewTransportCosts}
          loading={loading}
          onCancel={() => { if (returnContext) navigate(buildOrderTransportPath(returnContext.orderId)); else navigate(-1); }}
          onSubmit={handleSubmit}
        />
      </Card>
    </div>
  );
};

export default CreateTransportRoute;
